import type { CiJobConclusion, CiJobStatus } from '@developerz/domain';
import { IconActivity, IconAlert, IconCheck, IconClock, IconOctagonX } from '@developerz/ui';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { type CiRefusalReason, type CiRunRow, ciDuration } from '../lib/ci';
import { StatusPill, type StatusTone } from './StatusPill';
import { Button } from './ui/Button';

// One CI run as a .row-list row (spec §3.15, mock SimplePage '/ci'): icon tile +
// "repo — Passed · 2m 14s" title line, `runs-on` label badges, started meta, the
// `<2KB` failure-tail disclosure; ghost "View full log" on the right once a log
// archived. Presenter — the container owns the presign fetch behind `onViewLog`.

export type CiRunItemProps = {
  readonly run: CiRunRow;
  readonly onViewLog: (id: string) => void;
};

const STATUS_TONE: Record<CiJobStatus, StatusTone> = {
  queued: 'neutral',
  assigned: 'neutral',
  running: 'live',
  completed: 'neutral',
  refused: 'danger',
};

const STATUS_LABEL: Record<CiJobStatus, string> = {
  queued: 'ci.status_queued',
  assigned: 'ci.status_assigned',
  running: 'ci.status_running',
  completed: 'ci.status_completed',
  refused: 'ci.status_refused',
};

const CONCLUSION_TONE: Record<CiJobConclusion, StatusTone> = {
  success: 'success',
  failure: 'danger',
  neutral: 'neutral',
  cancelled: 'neutral',
  skipped: 'neutral',
  timed_out: 'danger',
  action_required: 'warning',
  stale: 'warning',
};

const CONCLUSION_LABEL: Record<CiJobConclusion, string> = {
  success: 'ci.conclusion_success',
  failure: 'ci.conclusion_failure',
  neutral: 'ci.conclusion_neutral',
  cancelled: 'ci.conclusion_cancelled',
  skipped: 'ci.conclusion_skipped',
  timed_out: 'ci.conclusion_timed_out',
  action_required: 'ci.conclusion_action_required',
  stale: 'ci.conclusion_stale',
};

// The tile's leading glyph (#4852): the row's outcome, not a constant check. Aligned
// to `CONCLUSION_TONE` / `CONCLUSION_LABEL` so a new conclusion is a type error here
// before it ships. Glyphs that read "passed" (a check) are gated on `success` only;
// a failure or a refusal is `IconOctagonX`, a warning is `IconAlert`, the idle/queued
// states and a no-conclusion `completed` are `IconClock`, a running run is
// `IconActivity`. The "a refused run with permission_missing should be a lock" idea
// in the issue's prose did not survive the test table — `IconOctagonX` reads more
// clearly than `IconLock` for a row the platform already refused.
const CONCLUSION_ICON: Record<CiJobConclusion, (p: object) => JSX.Element> = {
  success: IconCheck,
  failure: IconOctagonX,
  neutral: IconClock,
  cancelled: IconClock,
  skipped: IconClock,
  timed_out: IconOctagonX,
  action_required: IconAlert,
  stale: IconAlert,
};

const STATUS_ICON: Record<Exclude<CiJobStatus, 'completed'>, (p: object) => JSX.Element> = {
  queued: IconClock,
  assigned: IconClock,
  running: IconActivity,
  refused: IconOctagonX,
};

// A refused run's pill names WHY rather than showing a generic "refused" pill.
// ONE reason, because the platform produces one: `no_capacity` used to sit here
// too, and no `/v1` body has ever carried it — a job with no box big enough stays
// QUEUED and is audited, never refused (see `CiRefusalReason` in `lib/ci.ts`).
// These records are keyed by the domain enum, so a second reason cannot be
// rendered without first existing on the wire.
const REFUSAL_TONE: Record<CiRefusalReason, StatusTone> = {
  permission_missing: 'danger',
};

const REFUSAL_LABEL: Record<CiRefusalReason, string> = {
  permission_missing: 'ci.refusal_permission_missing',
};

// Finished → GitHub conclusion; refused → refusal reason (generic "Refused"
// only for rows predating the field); otherwise the lifecycle status.
function pill(run: CiRunRow): { tone: StatusTone; label: string } {
  if (run.status === 'completed' && run.conclusion) {
    return { tone: CONCLUSION_TONE[run.conclusion], label: CONCLUSION_LABEL[run.conclusion] };
  }
  if (run.status === 'refused' && run.refusal_reason) {
    return { tone: REFUSAL_TONE[run.refusal_reason], label: REFUSAL_LABEL[run.refusal_reason] };
  }
  return { tone: STATUS_TONE[run.status], label: STATUS_LABEL[run.status] };
}

// The tile's glyph, mirrored from `pill()`'s dispatch (#4852): the same `completed` +
// conclusion / else-status order, with the icon mapping per outcome. A refused run is
// `IconOctagonX` regardless of the refusal reason — the platform already refused, the
// glyph should not soften that. `completed` without a conclusion — a row the lifecycle
// closed but GitHub did not name — falls into the idle glyph (`IconClock`) the same way
// `queued` / `assigned` do.
function tileIcon(run: CiRunRow): (p: object) => JSX.Element {
  if (run.status === 'completed' && run.conclusion) return CONCLUSION_ICON[run.conclusion];
  if (run.status === 'completed') return IconClock;
  return STATUS_ICON[run.status];
}

export function CiRunItem(props: CiRunItemProps): JSX.Element {
  const p = (): { tone: StatusTone; label: string } => pill(props.run);
  return (
    <li class="row-list__row ci-run" data-status={props.run.status}>
      <div class="row-list__lead ci-run__lead">
        <span class="row-list__tile" aria-hidden="true">
          {(() => {
            const Icon = tileIcon(props.run);
            return <Icon />;
          })()}
        </span>
        <div class="ci-run__lines">
          <p class="ci-run__title">
            <span class="ci-run__repo">{props.run.repo_full_name}</span>
            <span class="ci-run__sep" aria-hidden="true">
              —
            </span>
            <StatusPill tone={p().tone}>{t(p().label)}</StatusPill>
            <Show when={ciDuration(props.run)}>
              {(d) => <span class="ci-run__duration">· {d()}</span>}
            </Show>
          </p>
          <p class="ci-run__labels">
            <For each={props.run.labels}>
              {(label) => <span class="badge badge--outline">{label}</span>}
            </For>
          </p>
          <p class="ci-run__meta">
            {t('ci.created', { when: props.run.created_iso.slice(0, 10) })}
          </p>
          <Show when={props.run.log_excerpt}>
            {(excerpt) => (
              <details class="ci-run__log">
                <summary>{t('ci.log_tail')}</summary>
                <pre class="code-block">{excerpt()}</pre>
              </details>
            )}
          </Show>
        </div>
      </div>
      <Show when={props.run.has_log}>
        <Button variant="ghost" size="sm" onClick={() => props.onViewLog(props.run.id)}>
          {t('ci.view_log')}
        </Button>
      </Show>
    </li>
  );
}
